/**
 * Stickers on the web (docs/stickers-plan.md D546–D566): the format's names,
 * the reader's «Animated stickers» switch, and the page's half of the player —
 * `clients/shared/stickers/sticker.js`, which `main.tsx` loads and configures.
 * The desktop's `components::stickers` keeps the same promises: the feed
 * stands while something covers it (D554), «once» starts over on every entry
 * into a chat (D565), and stickers rest on their posters while motion is off
 * or the switch is (D559).
 */
import type { Attachment, Message, Sticker } from "../gen/dto";

/** `echo_wire::sticker::TGS_MIME`. */
export const TGS_MIME = "application/x-tgsticker";
/** `echo_wire::sticker::STILL_MIMES` (D564). */
export const STILL_MIMES: readonly string[] = ["image/webp", "image/png"];
/** The long side of a sticker in the feed (D552, D568): no bubble, this box. */
export const FEED_SIDE = 320;
/** One to eight emoji per sticker (D566). */
export const MAX_EMOJI = 8;
/** A pack's title, in characters (D566). */
export const MAX_TITLE_CHARS = 64;
/** Stickers in a pack (D566). */
export const MAX_STICKERS = 120;
/** How many stickers a catalogue row shows of its pack (D566). */
export const PREVIEW_STICKERS = 5;
/** How many stickers one emoji alone in the box offers (D574). */
export const MAX_SUGGESTIONS = 10;
/** The poster's long side (D549). */
export const POSTER_SIDE = 320;
/** A still sticker (D564): its long side, and its bytes. */
export const STILL_MIN_SIDE = 128;
export const STILL_MAX_SIDE = 512;
export const STILL_MAX_BYTES = 128 * 1024;
/** The most of a picked file the editor reads — the desktop's
 * `runtime::stickers::PICKED_MAX_BYTES`. A Lottie JSON packs from 8 MiB at
 * most and a TGS is 64 KiB; a picture that is to become a 128 KiB sticker has
 * no business being larger. Refused by its size, before a byte is read — let
 * alone copied into the wasm, whose memory never shrinks, or decoded. */
export const PICKED_MAX_BYTES = 16 * 1024 * 1024;

/** How a sticker of `mime` is drawn — `null` for a kind this build cannot
 * name, which the feed draws as the «update» line (D552). The desktop's
 * `StickerFormat::of`. */
export function stickerFormat(mime: string): "tgs" | "still" | null {
  if (mime === TGS_MIME) return "tgs";
  return STILL_MIMES.includes(mime) ? "still" : null;
}

/** Whether a bubble can draw this sticker attachment: a format this build
 * knows. */
export function drawable(a: Attachment): boolean {
  return a.kind === "sticker" && stickerFormat(a.mime) !== null;
}

/** Whether a message is a sticker (D550) — of any format: it has no words
 * to edit, whether or not this build can draw it. */
export function isSticker(m: Message): boolean {
  return m.attachments?.[0]?.kind === "sticker";
}

/** The pack of the sticker a message is (D552), or null — what its menu's
 * «View pack» opens, as a click on the sticker does. */
export function stickerPackOf(m: Message): number | null {
  const a = m.attachments?.[0];
  return a?.kind === "sticker" && a.sticker != null ? a.sticker.pack_id : null;
}

/** The box a sticker of `width`×`height` takes inside a square of `side`: its
 * long side `side`, its short side by its aspect. */
export function fitted(width: number, height: number, side: number): [number, number] {
  const w = Math.max(1, width);
  const h = Math.max(1, height);
  return w >= h
    ? [side, Math.max(1, Math.floor((side * h) / w))]
    : [Math.max(1, Math.floor((side * w) / h)), side];
}

/** What «once» is kept by for a message's stickers (D565): its nonce, which the
 * bubble in flight and the confirmed one share, or its seq for a message with
 * none. A pack's link card under the message adds each sticker's file to it
 * (D575), the header over a reply `:reply` (D588). The desktop's
 * `stickers::play_key`. */
export function playKeyOf(channelId: number, seq: number, nonce: string): string {
  return nonce !== "" ? `${channelId}:${nonce}` : `${channelId}:${seq}`;
}

/** The sticker a reply answers (D588), from the quote it carries — none once
 * that is deleted, nor for a kind this build cannot draw. */
export function replySticker(m: Message): Attachment | null {
  const quote = m.reply_to;
  if (quote == null || quote.state !== "live") return null;
  const a = quote.attachments?.[0];
  return a !== undefined && drawable(a) ? a : null;
}

/** What a send's attachment is, built the way the gateway builds it from the
 * pack's row (D550) — for the bubble in flight. `echo_wire::dto::Sticker::
 * attachment`, field for field. */
export function attachmentOf(sticker: Sticker): Attachment {
  return {
    file_id: sticker.file_id,
    kind: "sticker",
    mime: sticker.mime,
    size: sticker.size,
    width: sticker.width,
    height: sticker.height,
    ...(sticker.duration_ms != null ? { duration_ms: sticker.duration_ms } : {}),
    thumb_file_id: sticker.poster_file_id,
    sticker: { pack_id: sticker.pack_id, emoji: sticker.emoji[0] ?? "" },
  };
}

// ---------------------------------------------------------------------------
// «Animated stickers» (D559): in this browser, per account — the web has no
// profile file, and the setting is the reader's, not the device's.

function animatedKey(userId: number): string {
  return `echofloat.stickers.animated.${userId}`;
}

/** Whether stickers move for this account in this browser — on unless the
 * reader turned them off here. */
export function animatedStickers(userId: number): boolean {
  try {
    return localStorage.getItem(animatedKey(userId)) !== "off";
  } catch {
    return true;
  }
}

export function setAnimatedStickers(userId: number, on: boolean): void {
  try {
    if (on) localStorage.removeItem(animatedKey(userId));
    else localStorage.setItem(animatedKey(userId), "off");
  } catch {
    // A browser that keeps nothing keeps nothing; the switch reads «on».
  }
  configureStill(userId);
}

// ---------------------------------------------------------------------------
// The player

interface Player {
  configure(options: Record<string, unknown>): void;
  pause(reason: string, on: boolean): void;
  chatEntered(channel: number): void;
  poster(
    src: string,
    box: number,
  ): Promise<{ blob: Blob; width: number; height: number; composition?: unknown }>;
}

function player(): Player | null {
  return (window as unknown as { EchoSticker?: Player }).EchoSticker ?? null;
}

/** Whether the reader asked for less motion: what the web has in place of
 * Windows battery saver (D559). */
function reducedMotion(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Starts the player (D553): its worker script and its engine from this
 * origin — the CSP admits no `blob:` worker, unlike the desktop's window — and
 * as many workers as the machine can spare beside the page and the voice
 * worker, two at most. Stickers move once an account says so
 * (`configureStill`). */
export function startStickerPlayer(workerUrl: string, wasmUrl: string): void {
  const cores = navigator.hardwareConcurrency || 4;
  player()?.configure({
    workerUrl,
    wasmUrl,
    workers: Math.min(2, Math.max(1, cores - 2)),
    blobWorker: false,
  });
}

/** Tells the player whether stickers rest on their posters for this account
 * (D552, D559). */
export function configureStill(userId: number): void {
  player()?.configure({ still: reducedMotion() || !animatedStickers(userId) });
}

/** The feed's stickers stand for `reason` — `panel`, `sheet` or `viewer` — or
 * go on (D554). */
export function pauseStickers(reason: "panel" | "sheet" | "viewer", on: boolean): void {
  player()?.pause(reason, on);
}

/** An entry into a chat (D565): «once» starts over. */
export function chatEntered(channel: number): void {
  player()?.chatEntered(channel);
}

/** Frame 0 of the TGS at `src` as a poster (D549), by the same engine the feed
 * plays with: WebP inside 64 KiB, PNG where the browser has no WebP encoder —
 * the blob says which. */
export async function makePoster(src: string): Promise<Blob> {
  const p = player();
  if (p === null) throw new Error("no sticker player");
  return (await p.poster(src, POSTER_SIDE)).blob;
}
